<template>
  <div class="following-page">
    <div class="page-heading">
      <div><h4>我的题材</h4><p>每日关注摘要 · {{ digestDate }} · {{ changedCount ? changedCount + ' 个题材今日有变化' : '今日暂无明显变化' }} · {{ unreadCount }} 条未读</p></div>
      <b-button size="sm" variant="outline-primary" to="/wonder/topic/table">发现题材</b-button>
    </div>

    <b-alert v-if="error" show variant="warning">{{ error }}</b-alert>
    <div v-if="loading" class="text-center py-5"><b-spinner></b-spinner></div>
    <div v-else-if="!error && !items.length" class="empty-state">
      <b-icon icon="bookmark" font-scale="2"></b-icon><strong>还没有关注题材</strong>
      <span>从首页强势题材或题材详情点击“关注”，这里会持续跟踪变化。</span>
    </div>
    <div v-else class="follow-grid">
      <article v-for="item in items" :key="item.topic_id" class="follow-card">
        <header>
          <router-link :to="topicLink(item)">{{ cleanName(item.name) }}</router-link>
          <button @click="unfollow(item)">取消关注</button>
        </header>
        <div class="score-row">
          <span>强度 <b>{{ value(item.score) }}</b></span><span>排名 <b>{{ item.rank ? '#' + item.rank : '--' }}</b></span>
          <span :class="pctClass(item.today_pct)">均涨 <b>{{ pct(item.today_pct) }}</b></span>
        </div>
        <div v-if="item.core_stocks && item.core_stocks.length" class="core-stocks">
          <div><strong>算法核心股</strong><small>截至 {{ coreQuoteTime(item) }}</small></div>
          <span v-for="stock in item.core_stocks" :key="stock.code" :title="coreReason(stock)">
            {{ stock.name }} <i>{{ value(stock.core_score) }}</i> <b :class="pctClass(stock.pct)">{{ pct(stock.pct) }}</b>
          </span>
        </div>
        <topic-daily-changes :changes="item.daily_changes || []" :digest-date="digestDate" />
        <div v-if="item.continuity && item.continuity.available" class="state-box">
          <strong :class="continuityStateClass(item.continuity.state)">{{ item.continuity.state }}</strong><span>持续 {{ value(item.continuity.continuity_score) }}</span>
          <p>{{ item.continuity.state_summary }}</p>
        </div>
        <div v-else class="state-box muted">历史评分不足，暂不形成持续判断。</div>
        <div class="card-footer-line">
          <span v-if="item.has_update" class="update-dot">有内容更新</span><span v-else>截至 {{ item.trade_date || '--' }}</span>
          <router-link :to="topicLink(item)">查看详情 →</router-link>
        </div>
        <div v-if="item.analysis_locked" class="unlock-line">
          完整五日轨迹、支持依据与风险条件为题材表格 VIP 权益
          <button @click="subscribe(item)">查看 ¥599 订阅</button>
        </div>
      </article>
    </div>

    <section v-if="alerts.length" class="alerts-section">
      <h5>最近变化 <b-badge v-if="rawUnreadCount" variant="danger">{{ rawUnreadCount }}</b-badge></h5>
      <div v-for="alert in visibleAlerts" :key="alert.id" :class="['alert-row', { unread: !alert.is_read }]">
        <time :datetime="alert.created_at || alert.trade_date">{{ alertTime(alert) }}</time><router-link :to="topicLink(alert)">{{ alert.title }}</router-link><p>{{ alert.summary }}</p>
      </div>
      <button v-if="alerts.length > 5" class="alerts-toggle" type="button" @click="alertsExpanded = !alertsExpanded">{{ alertsExpanded ? '收起' : `查看全部${alerts.length}条` }}</button>
    </section>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'
import TopicDailyChanges from '@/components/home/TopicDailyChanges.vue'
import { trackProductEvent } from '@/utils/productAnalytics'

export default {
  components: { TopicDailyChanges },
  name: 'WonderTopicFollowing',
  data() { return { loading: true, error: '', items: [], alerts: [], unreadCount: 0, rawUnreadCount: 0, digestDate: '', changedCount: 0, alertsExpanded: false } },
  computed: {
    visibleAlerts() { return this.alertsExpanded ? this.alerts : this.alerts.slice(0, 5) }
  },
  mounted() {
    if (!this.hasToken()) {
      this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
      return
    }
    this.load()
    this.$root.$on('topic-follow-alerts-read', this.load)
  },
  beforeDestroy() { this.$root.$off('topic-follow-alerts-read', this.load) },
  methods: {
    hasToken() { try { return Boolean(localStorage.getItem('token')) } catch (e) { return false } },
    cleanName(name) { return String(name || '').replace(/\s*[（(][^（）()]*[）)]/g, '').trim() },
    topicLink(item) { return { path: '/wonder/topic/detail/' + encodeURIComponent(item.unique_key || item.topic_id), query: { source: 'my_topics' } } },
    alertTime(alert) {
      const value = alert && (alert.created_at || alert.trade_date)
      return value ? String(value).replace('T', ' ').slice(0, 16) : '--'
    },
    coreQuoteTime(item) {
      const stock = item && item.core_stocks && item.core_stocks[0]
      return stock && (stock.quote_time || stock.quote_date) ? (stock.quote_time || stock.quote_date) : '--'
    },
    coreReason(stock) { return (stock && stock.reasons && stock.reasons.length) ? stock.reasons.join(' · ') : '动态核心评分' },
    currentTime() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
    },
    continuityStateClass(state) {
      if (state === '退潮迹象') return 'state-ebb'
      if (state === '转弱观察' || state === '高位分歧') return 'state-watch'
      if (state === '延续增强') return 'state-strong'
      if (state === '持续稳定') return 'state-stable'
      return 'state-neutral'
    },
    value(v) { return v === null || v === undefined ? '--' : Number(v).toFixed(1) },
    pct(v) { if (v === null || v === undefined) return '--'; const n = Number(v); return `${n > 0 ? '+' : ''}${n.toFixed(2)}%` },
    pctClass(v) { return Number(v) > 0 ? 'up' : Number(v) < 0 ? 'down' : '' },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const [followRes, alertRes] = await Promise.all([
          request_zzquant({ url: '/v3/topic/follows', noRedirect: true }),
          request_zzquant({ url: '/v3/topic/follow-alerts', noRedirect: true })
        ])
        this.items = followRes.data.data.items || []
        this.unreadCount = followRes.data.data.unread_count || 0
        this.rawUnreadCount = followRes.data.data.raw_unread_count || 0
        this.alerts = alertRes.data.data || []
        this.digestDate = followRes.data.data.digest_date || ''
        this.changedCount = followRes.data.data.changed_topic_count || 0
        this.loadCoreQuotes()
      } catch (e) {
        const status = e && (e.status || (e.response && e.response.status))
        if (status === 401 || e.__unauthorized) {
          try { localStorage.removeItem('token') } catch (ignore) { /* ignore */ }
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
        } else {
          this.error = '关注题材加载失败，请稍后重试。'
        }
      } finally { this.loading = false }
    },
    async loadCoreQuotes() {
      const names = [...new Set(this.items.reduce((result, item) => result.concat((item.core_stocks || []).map(stock => stock.name)), []))]
      if (!names.length) return
      try {
        const response = await request_zzquant({ url: '/v3/topic/table/stock-returns', method: 'POST', data: { stocks: names }, noRedirect: true })
        const quotes = response && response.data && response.data.code === 20000 ? response.data.data || {} : {}
        const quoteTime = this.currentTime()
        this.items = this.items.map(item => ({
          ...item,
          core_stocks: (item.core_stocks || []).map(stock => quotes[stock.name]
            ? { ...stock, pct: quotes[stock.name].px_change_rate, quote_time: quoteTime }
            : stock)
        }))
      } catch (e) { /* 保留后端最近交易日行情 */ }
    },
    async unfollow(item) { await request_zzquant({ url: `/v3/topic/follows/${item.topic_id}`, method: 'DELETE' }); await this.load(); this.$root.$emit('topic-follow-status-changed') },
    subscribe(item) {
      trackProductEvent('subscription_click', { topic_id: item.topic_id, source: 'my_topics', module_code: 'topic_kline' })
      this.$router.push({ path: '/subscription', query: { module: 'topic_kline', source: 'my_topics', topic_id: item.topic_id } })
    }
  }
}
</script>

<style scoped>
.following-page {
  --following-ink: var(--text-main, #263445);
  --following-muted: var(--text-muted, #718096);
  --following-border: var(--border-color, #dee5ec);
  --following-card-bg: var(--bg-card, #fff);
  --following-soft-bg: var(--bg-muted, #f5f7fa);
  --following-state-text: #536174;
  --following-up: #c84435;
  --following-down: #269c50;
  --following-update: #bd4938;
  --following-unlock-bg: #fff6e1;
  --following-unlock-text: #75603b;
  --following-unlock-link: #b06b00;
  --following-alert-border: #edf0f3;
  --following-unread-bg: #fff8f5;
  --following-state-stable: #2876a8;
  --following-state-strong: #b83d31;
  --following-state-watch: #c27618;
  --following-state-ebb: #b42318;
  padding: 10px 8px 24px;
  color: var(--following-ink);
}
.page-heading { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.page-heading h4 { margin:0 0 3px; font-weight:700; }
.page-heading p { margin:0; color:var(--following-muted); font-size:12px; }
.follow-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); align-items:start; gap:12px; }
.follow-card { width:100%; min-width:0; border:1px solid var(--following-border); border-radius:9px; background:var(--following-card-bg); padding:12px; }
.follow-card header,.score-row,.card-footer-line { display:flex; align-items:center; justify-content:space-between; gap:9px; }
.follow-card header a { color:var(--following-ink); font-size:16px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.follow-card header button { border:0; background:none; color:var(--following-muted); font-size:11px; white-space:nowrap; }
.score-row { margin:11px 0 8px; color:var(--following-muted); font-size:11px; }
.score-row b { color:var(--following-ink); font-size:14px; }
.score-row .up b { color:var(--following-up); }
.score-row .down b { color:var(--following-down); }
.core-stocks { margin:0 0 8px; padding:8px 9px; border:1px solid var(--following-border); border-radius:6px; background:var(--following-card-bg); display:flex; align-items:center; gap:8px 12px; flex-wrap:wrap; font-size:11px; }
.core-stocks > div { display:flex; align-items:baseline; gap:6px; margin-right:auto; }
.core-stocks small { color:var(--following-muted); }
.core-stocks span { white-space:nowrap; }
.core-stocks b { margin-left:2px; color:var(--following-ink); }
.core-stocks i { padding:1px 3px; border-radius:3px; background:var(--following-soft-bg); color:#3977a8; font-size:9px; font-style:normal; }
.core-stocks b.up { color:var(--following-up); }
.core-stocks b.down { color:var(--following-down); }
.state-box { min-height:82px; padding:9px; border-radius:6px; background:var(--following-soft-bg); }
.state-box strong.state-stable { color:var(--following-state-stable); }
.state-box strong.state-strong { color:var(--following-state-strong); }
.state-box strong.state-watch { color:var(--following-state-watch); }
.state-box strong.state-ebb { color:var(--following-state-ebb); }
.state-box strong.state-neutral { color:var(--following-muted); }
.state-box > span { float:right; color:var(--following-muted); font-size:11px; }
.state-box p { margin:6px 0 0; color:var(--following-state-text); font-size:12px; line-height:1.5; }
.state-box.muted { color:var(--following-muted); font-size:12px; }
.card-footer-line { margin-top:9px; color:var(--following-muted); font-size:11px; }
.update-dot { color:var(--following-update); }
.unlock-line { margin:9px -12px -12px; padding:8px 12px; background:var(--following-unlock-bg); color:var(--following-unlock-text); font-size:11px; }
.unlock-line button { float:right; border:0; background:none; color:var(--following-unlock-link); font-weight:700; }
.empty-state { display:flex; flex-direction:column; align-items:center; gap:8px; padding:55px; border:1px dashed var(--following-border); color:var(--following-muted); }
.alerts-section { margin-top:18px; }
.alerts-section h5 { font-weight:700; }
.alert-row { display:grid; grid-template-columns:125px 220px 1fr; gap:10px; padding:8px; border-top:1px solid var(--following-alert-border); color:var(--following-muted); font-size:12px; }
.alert-row p { margin:0; }
.alert-row.unread { background:var(--following-unread-bg); }
.alerts-toggle { display:block; margin:8px auto 0; border:0; background:none; color:#47749e; font-size:12px; cursor:pointer; }
@media(max-width:1000px){.follow-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}@media(max-width:650px){.follow-grid{grid-template-columns:minmax(0,1fr)}.alert-row{grid-template-columns:115px minmax(0,1fr)}.alert-row p{grid-column:1/-1}}
</style>

<style>
body.theme-dark .following-page {
  --following-border: #3b4554;
  --following-card-bg: #243247;
  --following-soft-bg: #2b3442;
  --following-state-text: #c6ceda;
  --following-up: #e88978;
  --following-down: #78c89a;
  --following-update: #e88978;
  --following-unlock-bg: #3b3428;
  --following-unlock-text: #d8bb77;
  --following-unlock-link: #e7bd6d;
  --following-alert-border: #334155;
  --following-unread-bg: #2d3644;
  --following-state-stable: #75b9e6;
  --following-state-strong: #f08b7d;
  --following-state-watch: #e7b45f;
  --following-state-ebb: #ff766c;
}
</style>
